import React, {Component, useCallback, useEffect, useMemo, useState} from 'react';
import 'react-toastify/dist/ReactToastify.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import './css/TempAuthPage.css';
import './css/App.css';
import './css/QuoteTable.css';
import AppProvider from "./context/AppProvider";
import QuoteToolClient from "@kjdelectronics/ps-quotetool-domain/service/QuoteToolClient";
import { redirectToLogin } from "./helper/auth.helper.js";
import { toast, ToastContainer } from "react-toastify";
import RouterManager from "./components/routes/RouterManager.js";
import {FALLBACK_CURRENCY_RATES} from "@kjdelectronics/ps-saturn-client/obj/CurrencyRates";
import DevWarningBanner from "./components/atoms/DevWarningBanner.js";

// User that do not have this role will not be granted access to the app

const App = () => {
    const [user, setUser] = useState(null);
    const [companies, setCompanies] = useState([]);
    const [countryData, setCountryData] = useState(null);
    const [currencyRates, setCurrencyRates] = useState(FALLBACK_CURRENCY_RATES);
    const [showErrorButton, setShowErrorButton] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [loadingError, setLoadingError] = useState(null);
    const totalTasks = 5; // Total number of API calls (added getCurrentUser)
    const [progress, setProgress] = useState(0);

    const IS_AUTH_ROUTE = window.location.pathname.startsWith("/auth");
    const IS_LOCALHOST_API_ROUTE = window.location.pathname.startsWith("/v1");

    /* REACT HOOKS */
    const quoteToolClient = useMemo(() => {
        return new QuoteToolClient({
            baseUrl: window.location.hostname === "localhost" ? "http://localhost:3001/v1" : null,
            token: null,
            unauthorizedCallback: () => {
                redirectToLogin();
            },
        });
    }, []); // only runs once on mount

    const initApp = useCallback(async () => {
        const updateProgress = () => setProgress(prev => prev + 100 / totalTasks);

        try {
            await Promise.all([
                quoteToolClient.getCurrentUser().then(currentUser => {
                    console.log("Current user:", currentUser);
                    setUser(currentUser);
                    updateProgress();
                }).catch(err => {
                    // If getCurrentUser fails (401), user is not authenticated
                    // RouterManager will handle redirect logic based on route
                    console.log("User not authenticated");
                    setUser(null);
                    updateProgress();
                }),
                quoteToolClient.getSaturnCompanies().then(companiesResult => {
                    console.log(companiesResult);
                    setCompanies(companiesResult.data || companiesResult);
                    updateProgress();
                }),
                quoteToolClient.getSaturnCountries().then(countries => {
                    setCountryData(prev => ({ ...prev, countries }));
                    updateProgress();
                }),
                quoteToolClient.getSaturnCountrySubdivisions().then(subdivisions => {
                    setCountryData(prev => ({ ...prev, countrySubdivisions: subdivisions }));
                    updateProgress();
                }),
                quoteToolClient.getSaturnCurrencyRates().then(currencyRates => {
                    setCurrencyRates(currencyRates);
                    updateProgress();
                })
            ]);

            setIsLoading(false); // Hide loading state when all promises resolve
        } catch (error) {
            console.error("Error fetching data:", error);
            toast.error("Failed to load application data.");
            setIsLoading(false); // End loading on error
            setLoadingError(error); // End loading on error
        }
    }, [quoteToolClient]);


    useEffect(() => {
        initApp();
    }, [initApp, IS_AUTH_ROUTE, IS_LOCALHOST_API_ROUTE]);

    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === 'Pause') {
                setShowErrorButton(prev => !prev);
            }
        };

        const handleBeforeUnload = (event) => {
            if (event.ctrlKey && event.shiftKey && event.key === 'R') {
                localStorage.removeItem('termsOptions');
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, []);

    /* RENDER */
    console.log("APP.js");

    return (

        <AppProvider user={user} countryData={countryData} companies={companies} currencyRates={currencyRates}>
            <DevWarningBanner user={user} />
            <RouterManager
                isAuthRoute={IS_AUTH_ROUTE}
                isLocalHostApiRoute={IS_LOCALHOST_API_ROUTE}
                quoteToolClient={quoteToolClient}
                user={user}
                showErrorButton={showErrorButton}
                isLoading={isLoading}
                error={loadingError}
                loadingProgress={progress}
            />
            <ToastContainer autoClose={2000}/>
        </AppProvider>
    );
};

/* Dark Mode WIP do not activate without changing the way the colors invert
const AppWithDarkMode = () => {
    const [isDarkMode, setIsDarkMode] = useState(false);

    useEffect(() => {
        const darkModeMediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
        setIsDarkMode(darkModeMediaQuery.matches);

        const handleChange = (e) => {
            setIsDarkMode(e.matches);
        };

        darkModeMediaQuery.addEventListener('change', handleChange);
        return () => darkModeMediaQuery.removeEventListener('change', handleChange);
    }, []);

    return (
        <div className={isDarkMode ? 'dark-mode' : ''}>
            <App />
        </div>
    );
};

Use when ready: export default AppWithDarkMode;

 */

export default App;